import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { catchError, map, Observable, switchMap, take, throwError } from 'rxjs';
import { REDIRECT_REQUEST } from 'src/app/app.module';
import { logoutUser } from 'src/app/auth/state/actions/auth.actions';
import { selectAuthUser } from 'src/app/auth/state/selectors/auth.selectors';
import { environment } from 'src/environments/environment';
import { User } from '../models/users';
import { GlobalErrorHandlerService } from '../services/global-error-handler.service';
import { SKIP_ENCODE_URL, SKIP_GLOBAL_ERROR_HANDLER } from './http-context-tokens';
import { unsetLoading } from 'src/app/loading/state/actions/loading.actions';

@Injectable({
  providedIn: 'root',
})
export class HttpTokenInterceptor implements HttpInterceptor {
  private store = inject(Store);
  private router = inject(Router);
  private globalErrorHandlerService = inject(GlobalErrorHandlerService);
  private snackbar = inject(MatSnackBar);


  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    if (req?.url) {
      if (
        req.url.indexOf('amazonaws') !== -1 &&
        req.url.indexOf(environment.RH_BASE_URL) === -1
      ) {
        return next.handle(req);
      }
    }

    let { headers } = req;
    let { url } = req;

    // if request has authorization header send it to the request to the next handler
    if (req.headers.has('Authorization')) {
      return next.handle(
        req.clone({
          withCredentials: true,
          url: req.context.get(SKIP_ENCODE_URL) ? url : encodeURI(url),
          headers: req.headers.append('No-Auth-Challenge', 'true'),
        })
      );
    }

    return this.store.select(selectAuthUser).pipe(
      take(1),
      map((user: User.User) => {
        if (user?.authToken) {
          const authorizationHeader = `Basic ${btoa(
            `${user._id}:${user.authToken}`
          )}`;
          headers = headers.set('Authorization', authorizationHeader);
          headers = headers.set('No-Auth-Challenge', 'true');
        }

        return req.clone({
          withCredentials: true,
          url: req.context.get(SKIP_ENCODE_URL) ? url : encodeURI(url),
          headers,
        });
      }),
      switchMap((newRequest: HttpRequest<any>) =>
        next.handle(newRequest).pipe(
          catchError((error: HttpErrorResponse) => {
            if (!req.context.get(SKIP_GLOBAL_ERROR_HANDLER)) {
              this.errorHandler(error, req);
              throw error;
            }
            console.error('[Global Error Handler] ', error);
            return throwError(() => error);
          })
        )
      )
    );
  }

  private errorHandler(error: HttpErrorResponse, req: HttpRequest<any>) {
    this.store.dispatch(unsetLoading());

    switch (error.status) {
      case 401:
        // this action has an effect associated with it that clears user tenant
        // and redirects to the signin page
        if (this.router.url !== '/signin') {
          this.snackbar.open(
            'La tua sessione è scaduta. Devi autenticarti nuovamente.',
            'x',
            {
              duration: 5000,
            }
          );
        }
        this.store.dispatch(logoutUser());

        break;
      case 403: // FORBIDDEN
        // this.store.dispatch(handleHttpError({status: 403}));
        this.snackbar.open(
          'Non sei autorizzato a fare questa richiesta.',
          'x',
          { duration: 5000 }
        );
        this.store.dispatch(logoutUser());
        break;
      case 404: // NOT FOUND - DO NOT REMOVE!!!
        console.log('NOT FOUND MUST be handled by subsciption');
        if (req.context.get(REDIRECT_REQUEST)) {
          this.router.navigate(['not-found']);
        }
        break;
      case 409:
        this.snackbar.open(
          'La risorsa che stai cercando di creare esiste già.',
          'x',
          {
            duration: 50000,
          }
        );
        break;
      case 417:
        return;
      case 429:
        this.snackbar.open(
          'Stiamo riscontrando delle attività anomale sul tuo account, riprova più tardi',
          'x',
          { duration: 3000 }
        );
        this.store.dispatch(logoutUser());
        break;
      case 0: // NO NETWORK
        this.snackbar.open('Sei offline.', 'x', { duration: 5000 });
        this.store.dispatch(logoutUser());
        break;
      default:
        // throw new Error('test');
        if (error) {
          this.globalErrorHandlerService.set(error);
          this.router.navigate(['error']);
        }
        break;
    }
  }
}
